/**
 * Dockview overflow-tab popover placement (架构方案 §5.7).
 *
 * Dockview `PopupService.openPopover` pins the menu's top-left to the click
 * point, then `shiftAbsoluteElementIntoView` translates it when it overflows
 * the root. On the right-hand auxiliaryBar that lands the list over the tab
 * titles. After the library opens the popover we re-anchor it to
 * `.dv-tabs-overflow-dropdown-root`: below the trigger, end-aligned, then
 * clamped to the dockview root.
 */
export const OVERFLOW_POPOVER_GAP = 0;
export const OVERFLOW_POPOVER_INSET = 10;

const OVERFLOW_TRIGGER = '.dv-tabs-overflow-dropdown-root';
const OVERFLOW_MENU = '.dv-popover-anchor .dv-tabs-overflow-container';
const TAB_CLOSE_CONTROL = '[role="button"]';
const TAB_CLOSE_SCOPE = '.dv-tab, .dv-tabs-overflow-container';

interface ClosestNode {
  closest(selector: string): ClosestNode | null;
}

function hasClosest(
  target: EventTarget | null,
): target is EventTarget & ClosestNode {
  return (
    target !== null &&
    typeof target === 'object' &&
    'closest' in target &&
    typeof target.closest === 'function'
  );
}

/**
 * Tab close controls call `stopPropagation` on pointerdown and click so Dockview
 * does not activate the tab. That also stops the overflow row and
 * `PopupService`'s window listener, so an open ∨ N menu stays up. The menu is
 * removed after the click handler runs: doing it during capture would detach
 * the button before `click`, and the tab would not close.
 */
export function isTabCloseControl(target: EventTarget | null): boolean {
  if (!hasClosest(target)) {
    return false;
  }
  const button = target.closest(TAB_CLOSE_CONTROL);
  return button?.closest(TAB_CLOSE_SCOPE) != null;
}

function dismissOpenOverflowPopover(): void {
  const menu = document.querySelector(OVERFLOW_MENU);
  const wrapper = menu?.parentElement;
  if (wrapper instanceof HTMLElement) {
    wrapper.remove();
  }
}

export interface OverflowBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function overflowPopoverOffset(input: {
  trigger: OverflowBox;
  menu: { width: number; height: number };
  anchor: { left: number; top: number };
  root: OverflowBox;
  gap?: number;
  inset?: number;
}): { left: number; top: number } {
  const gap = input.gap ?? OVERFLOW_POPOVER_GAP;
  const inset = input.inset ?? OVERFLOW_POPOVER_INSET;
  const { trigger, menu, anchor, root } = input;

  const minLeftVp = root.left + inset;
  const maxLeftVp = root.right - inset - menu.width;
  let leftVp = trigger.right - menu.width;
  leftVp =
    maxLeftVp < minLeftVp
      ? minLeftVp
      : Math.min(Math.max(leftVp, minLeftVp), maxLeftVp);

  const below = trigger.bottom + gap;
  const above = trigger.top - gap - menu.height;
  const fitsBelow = below + menu.height <= root.bottom - inset;
  const fitsAbove = above >= root.top + inset;
  let topVp = !fitsBelow && fitsAbove ? above : below;

  const minTopVp = root.top + inset;
  const maxTopVp = root.bottom - inset - menu.height;
  topVp =
    maxTopVp < minTopVp
      ? minTopVp
      : Math.min(Math.max(topVp, minTopVp), maxTopVp);

  return {
    left: leftVp - anchor.left,
    top: topVp - anchor.top,
  };
}

function placeOverflowPopover(
  wrapper: HTMLElement,
  trigger: HTMLElement,
): void {
  const anchor = wrapper.offsetParent;
  const root = anchor instanceof HTMLElement ? anchor.parentElement : null;
  if (!(anchor instanceof HTMLElement) || !(root instanceof HTMLElement)) {
    return;
  }
  const pos = overflowPopoverOffset({
    trigger: trigger.getBoundingClientRect(),
    menu: { width: wrapper.offsetWidth, height: wrapper.offsetHeight },
    anchor: anchor.getBoundingClientRect(),
    root: root.getBoundingClientRect(),
  });
  wrapper.style.top = `${pos.top}px`;
  wrapper.style.left = `${pos.left}px`;
  wrapper.style.transform = 'none';
}

/**
 * Listen for overflow-button clicks and re-anchor the library popover after
 * Dockview's own `requestAnimationFrame` clamp. A tab close click also drops
 * an open menu once its own handler has run. Returns a dispose handle for
 * `useDockviewManager` unmount.
 */
export function installOverflowPopoverAnchor(): { dispose(): void } {
  let raf = 0;
  let disposed = false;
  const onTabCloseClick = (event: MouseEvent): void => {
    if (!isTabCloseControl(event.target)) {
      return;
    }
    queueMicrotask(() => {
      if (!disposed) {
        dismissOpenOverflowPopover();
      }
    });
  };
  const onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const trigger = target.closest(OVERFLOW_TRIGGER);
    if (!(trigger instanceof HTMLElement)) {
      return;
    }
    if (raf !== 0) {
      cancelAnimationFrame(raf);
    }
    raf = requestAnimationFrame(() => {
      raf = 0;
      const menu = document.querySelector(OVERFLOW_MENU);
      const wrapper = menu?.parentElement;
      if (!(wrapper instanceof HTMLElement)) {
        return;
      }
      placeOverflowPopover(wrapper, trigger);
    });
  };
  document.addEventListener('click', onTabCloseClick, true);
  document.addEventListener('click', onClick);
  return {
    dispose: () => {
      disposed = true;
      document.removeEventListener('click', onTabCloseClick, true);
      document.removeEventListener('click', onClick);
      if (raf !== 0) {
        cancelAnimationFrame(raf);
      }
    },
  };
}
